<template>
<div class="pl-pb pl-root">
    <div v-if="!data && loading" class="pl-pb-state">Loading the people board…</div>
    <div v-else-if="!data && errorMsg" class="pl-pb-state pl-pb-state-err">{{ errorMsg }}</div>

    <template v-else-if="data">
        <div class="pl-pb-wrap" :class="{ 'pl-pb-busy': loading }">
            <div class="pl-pb-grid" :style="gridStyle">

                <!-- header row: range label, then one head per person (+ Off the team) -->
                <div class="pl-pb-hd pl-pb-hd-first">
                    <b>{{ rangeTitle }}</b>
                    <small>{{ people.length }} on the team</small>
                </div>
                <div v-for="col in columns" :key="'hd' + col.key"
                     class="pl-pb-hd" :class="{ 'pl-pb-hd-off': col.off }"
                     :title="col.off ? 'Slots held by people without the production role: ' + offTeamNames : col.person.name">
                    <template v-if="!col.off">
                        <span class="pl-pb-av">{{ initials(col.person.name) }}</span>
                        <span class="pl-pb-hd-tx">
                            <b>{{ col.person.name }}</b>
                            <small v-if="col.person.auto_fill_percent !== null">auto-fill to {{ col.person.auto_fill_percent }}%</small>
                            <small v-else>no auto-fill line</small>
                        </span>
                    </template>
                    <template v-else>
                        <span class="pl-pb-hd-tx">
                            <b>Off the team</b>
                            <small>{{ offTeamNames }}</small>
                        </span>
                    </template>
                </div>

                <!-- one row per week -->
                <template v-for="w in data.weeks">
                    <div :key="'wk' + w.start" class="pl-pb-wk"
                         :class="{ 'pl-pb-cur': w.is_current, 'pl-pb-past': isPast(w) }">
                        <b>{{ w.label }}</b>
                        <small>{{ w.we }}</small>
                        <span v-if="w.is_current" class="pl-pb-now">this week</span>
                    </div>

                    <div v-for="col in columns" :key="col.key + '|' + w.start"
                         class="pl-pb-cell"
                         :class="{ 'pl-pb-cur': w.is_current, 'pl-pb-past': isPast(w), 'pl-pb-cell-off': col.off }">

                        <div class="pl-pb-ch" :class="headBand(col, w)" :title="headTitle(col, w)">
                            <div class="pl-pb-ch-r1">
                                <span class="pl-pb-ch-h">{{ headLine(col, w) }}</span>
                                <span class="pl-pb-ch-n">{{ jobCount(col, w) }}</span>
                            </div>
                            <span v-if="!col.off" class="pl-cap-track pl-pb-track">
                                <span class="pl-cap-fill" :style="{ width: fillPct(col, w) + '%' }"></span>
                                <span v-if="col.person.auto_fill_percent !== null" class="pl-cap-lim"
                                      :style="{ left: Math.min(col.person.auto_fill_percent, 100) + '%' }"></span>
                            </span>
                            <div class="pl-pb-ch-free" :class="freeClass(col, w)">{{ freeLine(col, w) }}</div>
                        </div>

                        <draggable class="pl-pb-list"
                                   :list="listFor(col, w)"
                                   :group="col.off ? { name: 'pl-people', put: false } : 'pl-people'"
                                   :disabled="!canDrag"
                                   :data-user-id="col.off ? '' : col.user_id"
                                   :data-week-start="w.start"
                                   v-bind="dragOpts"
                                   @start="onDragStart"
                                   @end="onDragEnd"
                                   @change="onCellChange(col, w, $event)">
                            <job-card v-for="card in visibleCards(col, w)" :key="card.key"
                                      :job="card"
                                      :hours="card.planned_seconds"
                                      :colour="cardColour(card)"
                                      :muted="isPast(w)"
                                      :clickable="!!card.quote_id"
                                      :extra="cardExtra(card)"
                                      @open="onOpen(card)" />
                            <template slot="footer">
                                <button v-if="hiddenCount(col, w) > 0" type="button" class="pl-pb-more pl-no-drag"
                                        @click="expand(col, w)">+ {{ hiddenCount(col, w) }} more</button>
                                <div v-else-if="showFree(col, w)" class="pl-pb-free">— free —</div>
                            </template>
                        </draggable>
                    </div>
                </template>
            </div>
        </div>

        <div class="pl-pb-legend">
            <span v-for="wc in legendWcs" :key="'lg' + wc.id"><i :style="{ background: wc.colour }"></i>{{ wc.name }}</span>
            <span><i class="pl-pb-lg-typed"></i>delivery / ad-hoc</span>
            <span class="pl-pb-legend-hint">hatched = still in pre-production · notch = auto-fill line · past weeks dimmed</span>
        </div>
    </template>
</div>
</template>

<script>
import draggable from 'vuedraggable';
import JobCard from './JobCard';
import { DRAG_OPTS, wcLookups, fmtSecs, adjustedPct, bandClass, initials, errMsg } from './shared';

/**
 * P3 People Board — CONTRACT (implemented by the P3 stream; PlannerPage
 * mounts it and owns every server write except the board's own GET).
 *
 * Props
 *   start        Y-m-d Monday of the first row
 *   weeks        number of week rows (1 / 4 / month's 5–6)
 *   rangeMode    'week' | 'month' | '28d' — for labels only
 *   board        the /planner/board payload (workcentres for colours, people, today, permissions)
 *   permissions  board.permissions (can_replan / can_place gate the drag)
 *   flagFilter   null | 'artwork_waiting' | 'priority' — hide cards that don't match
 *   refreshKey   bumped by PlannerPage after any board write or Echo signal → refetch
 *
 * Data: GET /planner/people-board?start=&weeks=  (QAPP\PeopleBoard contract)
 *
 * Emits
 *   bulk-move    { slot_ids: [int], slots: [the cell's slot snapshots], target: { user_id?, days? },
 *                  label: 'Alastair Gabriel → Pete M, w/c 14 Sep' }
 *                — a card dropped on another (person, week) cell. PlannerPage dry-runs, then
 *                commits with the receipt + Undo, or opens the bulk panel pre-loaded with
 *                `slots` + `target` if anything is refused. After emitting, restore the card to
 *                its source cell client-side (splice it back) and wait for refreshKey.
 *   open-job     quote_id — PlannerPage sets quoteFilter so the "where it's up to" panel opens
 *   error        message string
 *
 * Layout: columns = people (+ "Off the team" when any on_team=false), rows = weeks.
 * Cell header: planned vs capacity for that person/week, a bar with a notch at
 * auto_fill_percent, "Nh free" headline. Cards = JobCard (hours = planned_seconds in
 * that cell; colour = workcentre colour of the cell's first slot). Past weeks dimmed.
 * Drag with vuedraggable + DRAG_OPTS from shared.js, group 'pl-people', one list per cell;
 * on @change added → compute target from the destination cell vs the source cell:
 *   same week, other person   → { user_id }
 *   same person, other week   → { days: 7 * Δweeks }
 *   both                      → { user_id, days }
 * then emit bulk-move and immediately refetch on the next refreshKey bump (PlannerPage
 * bumps it when the move lands). Never mutate the server from here except the GET.
 *
 * The "Off the team" column only gives: cards can be dragged OUT of it onto a
 * real person (that is the whole point of surfacing stray slots) but nothing
 * can be dropped INTO it — with several off-team users there is no single
 * user_id a drop could mean.
 */
const CARD_CAP = 6;

export default {
    name: 'PeopleBoard',
    components: { draggable, JobCard },

    props: {
        start: { type: String, required: true },
        weeks: { type: Number, required: true },
        rangeMode: { type: String, default: '28d' },
        board: { type: Object, required: true },
        permissions: { type: Object, required: true },
        flagFilter: { type: String, default: null },
        refreshKey: { type: Number, default: 0 },
    },

    data() {
        return {
            data: null,        // the /planner/people-board payload
            loading: false,
            errorMsg: null,
            lists: {},         // cellKey -> mutable card array (vuedraggable owns these)
            totals: {},        // cellKey -> { planned, done, jobs } from EVERY cell, filter or not
            expanded: {},      // cellKey -> true once "+ n more" was clicked
            dragging: false,
        };
    },

    created() {
        this.fetch();
    },

    watch: {
        start() { this.fetch(); },
        weeks() { this.fetch(); },
        refreshKey() { this.fetch(); },
        flagFilter() { this.rebuildLists(); },
    },

    computed: {
        wc() {
            return wcLookups(this.board ? this.board.workcentres : []);
        },
        dragOpts() {
            return DRAG_OPTS;
        },
        canDrag() {
            return !!(this.permissions && (this.permissions.can_replan || this.permissions.can_place));
        },
        people() {
            return this.data ? this.data.people.filter(p => p.on_team) : [];
        },
        offTeam() {
            return this.data ? this.data.people.filter(p => !p.on_team) : [];
        },
        offTeamNames() {
            return this.offTeam.map(p => p.name).join(', ');
        },
        columns() {
            const cols = this.people.map(p => ({ key: 'u' + p.id, user_id: p.id, person: p, off: false }));
            if (this.offTeam.length) {
                cols.push({ key: 'off', user_id: null, person: null, off: true });
            }
            return cols;
        },
        gridStyle() {
            return { gridTemplateColumns: '160px repeat(' + Math.max(1, this.columns.length) + ', minmax(190px, 1fr))' };
        },
        rangeTitle() {
            const weeks = this.data ? this.data.weeks : [];
            if (!weeks.length) return '';
            if (this.rangeMode === 'week') return weeks[0].label;
            if (this.rangeMode === 'month') {
                const mid = weeks[Math.floor(weeks.length / 2)];
                return new Date(mid.start).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
            }
            return weeks.length + ' weeks from ' + weeks[0].label.replace(/^w\/c /, '');
        },
        legendWcs() {
            const wcs = (this.board && this.board.workcentres) || [];
            return wcs.map(w => ({ id: w.id, name: w.name, colour: this.wc.colour(w.id, 'process') }));
        },
    },

    methods: {
        initials,
        fmtSecs,

        // ---------- data ----------
        fetch() {
            if (!this.start || !this.weeks) return;
            this.loading = true;
            const seq = this._seq = (this._seq || 0) + 1;
            window.axios.get('/planner/people-board', { params: { start: this.start, weeks: this.weeks } })
                .then(res => {
                    if (seq !== this._seq) return;   // superseded by a newer fetch
                    // Never rebuild the lists mid-drag — Sortable holds DOM
                    // nodes inside them. Apply once the card is put down.
                    if (this.dragging) { this._pending = res.data; return; }
                    this.apply(res.data);
                })
                .catch(err => {
                    if (seq !== this._seq) return;
                    const msg = errMsg(err);
                    this.errorMsg = msg;
                    this.$emit('error', msg);
                })
                .then(() => { if (seq === this._seq) this.loading = false; });
        },
        apply(data) {
            this.data = data;
            this.errorMsg = null;
            this.rebuildLists();
        },

        // ---------- cells ----------
        cellKey(col, w) {
            return (col.off ? 'off' : col.user_id) + '|' + w.start;
        },
        rebuildLists() {
            const lists = {};
            const totals = {};
            if (!this.data) { this.lists = lists; this.totals = totals; return; }

            const offIds = {};
            this.offTeam.forEach(p => { offIds[p.id] = true; });
            this.columns.forEach(col => this.data.weeks.forEach(w => {
                lists[this.cellKey(col, w)] = [];
                totals[this.cellKey(col, w)] = { planned: 0, done: 0, jobs: 0 };
            }));

            this.data.cells.forEach(cell => {
                const key = (offIds[cell.user_id] ? 'off' : cell.user_id) + '|' + cell.week_start;
                if (!lists[key]) { lists[key] = []; totals[key] = { planned: 0, done: 0, jobs: 0 }; }
                totals[key].planned += cell.planned_seconds || 0;
                totals[key].done += cell.done_seconds || 0;
                totals[key].jobs += 1;
                if (!this.passesFilter(cell)) return;
                const first = cell.slots && cell.slots[0];
                const ident = cell.type === 'process' ? 'q' + cell.quote_id : 's' + (first ? first.id : '');
                lists[key].push(Object.assign({ key: cell.user_id + ':' + ident }, cell));
            });

            Object.keys(lists).forEach(k => lists[k].sort(this.cmpCards));
            this.lists = lists;
            this.totals = totals;
        },
        cmpCards(a, b) {
            const da = a.deadline || '9999-12-31';
            const db = b.deadline || '9999-12-31';
            if (da !== db) return da < db ? -1 : 1;
            return String(a.quote_name || '').localeCompare(String(b.quote_name || ''));
        },
        passesFilter(cell) {
            if (!this.flagFilter) return true;
            const f = cell.flags || {};
            if (this.flagFilter === 'artwork_waiting') return f.artwork === 'waiting';
            if (this.flagFilter === 'priority') return !!f.priority;
            return true;
        },
        listFor(col, w) {
            const key = this.cellKey(col, w);
            if (!this.lists[key]) this.$set(this.lists, key, []);
            return this.lists[key];
        },
        totalsFor(col, w) {
            return this.totals[this.cellKey(col, w)] || { planned: 0, done: 0, jobs: 0 };
        },
        visibleCards(col, w) {
            const list = this.listFor(col, w);
            if (this.expanded[this.cellKey(col, w)] || list.length <= CARD_CAP) return list;
            return list.slice(0, CARD_CAP);
        },
        hiddenCount(col, w) {
            return this.listFor(col, w).length - this.visibleCards(col, w).length;
        },
        expand(col, w) {
            this.$set(this.expanded, this.cellKey(col, w), true);
        },
        showFree(col, w) {
            return !col.off && !this.isPast(w) && !this.listFor(col, w).length
                && this.totalsFor(col, w).planned <= 0 && this.capacityHours(col, w) > 0;
        },
        isPast(w) {
            return !!(this.data && w.end < this.data.today);
        },

        // ---------- cell header ----------
        hrs(secs) {
            return String(Math.round(secs / 360) / 10);
        },
        capacityHours(col, w) {
            if (col.off || !col.person) return 0;
            return Number((col.person.capacity || {})[w.start] || 0);
        },
        plannedSecs(col, w) {
            return this.totalsFor(col, w).planned;
        },
        pct(col, w) {
            return adjustedPct(this.plannedSecs(col, w), Math.round(this.capacityHours(col, w) * 3600), this.data.efficiency_percent);
        },
        fillPct(col, w) {
            return Math.min(100, this.pct(col, w));
        },
        overLine(col, w) {
            if (col.off || !col.person) return false;
            const af = col.person.auto_fill_percent;
            return af !== null && af !== undefined && this.capacityHours(col, w) > 0 && this.pct(col, w) > af;
        },
        headBand(col, w) {
            if (col.off) return '';
            const planned = this.plannedSecs(col, w);
            if (this.capacityHours(col, w) <= 0) return planned > 0 ? 'pl-b-tight' : 'pl-b-low';
            return bandClass(this.pct(col, w));
        },
        headLine(col, w) {
            const planned = this.plannedSecs(col, w);
            if (col.off) return planned > 0 ? this.hrs(planned) + 'h' : '—';
            const cap = this.capacityHours(col, w);
            if (cap <= 0 && planned <= 0) return '—';
            return this.hrs(planned) + 'h / ' + this.hrs(cap * 3600) + 'h';
        },
        jobCount(col, w) {
            const n = this.totalsFor(col, w).jobs;
            if (!n) return '';
            return n + (n === 1 ? ' job' : ' jobs');
        },
        freeLine(col, w) {
            const planned = this.plannedSecs(col, w);
            if (col.off) return planned > 0 ? 'nobody is working these' : '';
            const cap = this.capacityHours(col, w);
            if (cap <= 0) return planned > 0 ? 'no set hours this week' : '';
            let s = this.hrs(Math.max(0, cap * 3600 - planned)) + 'h free';
            if (this.overLine(col, w)) s += ' · over the ' + col.person.auto_fill_percent + '% line';
            return s;
        },
        freeClass(col, w) {
            if (col.off) return this.plannedSecs(col, w) > 0 ? 'pl-pb-over' : 'pl-pb-none';
            const cap = this.capacityHours(col, w);
            if (cap <= 0) return this.plannedSecs(col, w) > 0 ? 'pl-pb-warn' : 'pl-pb-none';
            if (this.pct(col, w) > 100) return 'pl-pb-over';
            if (this.overLine(col, w)) return 'pl-pb-warn';
            return '';
        },
        headTitle(col, w) {
            const t = this.totalsFor(col, w);
            const parts = [this.hrs(t.planned) + 'h planned'];
            if (t.done) parts.push(this.hrs(t.done) + 'h done');
            if (!col.off) parts.push(this.hrs(this.capacityHours(col, w) * 3600) + 'h capacity');
            return parts.join(' · ');
        },

        // ---------- cards ----------
        cardColour(card) {
            const first = card.slots && card.slots[0];
            if (!first) return this.wc.colour(null, card.type);
            return this.wc.colour(first.workcentre_id, first.type);
        },
        cardExtra(card) {
            return card.done_seconds ? fmtSecs(card.done_seconds) + ' done' : null;
        },
        onOpen(card) {
            if (card.quote_id) this.$emit('open-job', card.quote_id);
        },

        // ---------- drag = reassign and/or move a week ----------
        onDragStart() {
            this.dragging = true;
        },
        onDragEnd() {
            this.dragging = false;
            if (this._pending) {
                const d = this._pending;
                this._pending = null;
                this.apply(d);
            }
        },
        weekIndex(start) {
            const weeks = this.data ? this.data.weeks : [];
            for (let i = 0; i < weeks.length; i++) {
                if (weeks[i].start === start) return i;
            }
            return -1;
        },
        onCellChange(col, w, evt) {
            // The server is authoritative: whatever Sortable did to the local
            // arrays, snap every card back to the last payload. PlannerPage
            // bumps refreshKey once the move lands and the refetch shows the
            // truth. (Sortable fires add→remove synchronously; the rebuild
            // waits a tick so both have finished.)
            this.$nextTick(this.rebuildLists);

            const info = evt.added;
            if (!info || !info.element || col.off) return;   // evt.removed / evt.moved: nothing to send
            const card = info.element;

            const target = {};
            if (card.user_id !== col.user_id) target.user_id = col.user_id;
            const dw = this.weekIndex(w.start) - this.weekIndex(card.week_start);
            if (dw !== 0) target.days = 7 * dw;
            if (Object.keys(target).length === 0) return;   // dropped back where it came from

            const slots = card.slots || [];
            if (!slots.length) return;
            this.$emit('bulk-move', {
                slot_ids: slots.map(s => s.id),
                slots: slots,
                target: target,
                label: (card.quote_name || 'Job') + ' → ' + col.person.name + ', ' + w.label,
            });
        },
    },
};
</script>

<style scoped>
.pl-pb{margin-top:6px}
.pl-pb-state{color:#898781; font-weight:600; padding:30px 0; text-align:center}
.pl-pb-state-err{color:#d03b3b}
.pl-pb-wrap{overflow-x:auto; padding-bottom:4px}
.pl-pb-busy{opacity:.85; transition:opacity .15s}

/* inline-grid so the box grows with its 190px-minimum tracks and the
   wrapper scrolls, instead of tracks spilling past the border */
.pl-pb-grid{display:inline-grid; min-width:100%; vertical-align:top;
    background:#fcfcfb; border:1px solid #e1e0d9; border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); overflow:hidden}

/* ---- header row ---- */
.pl-pb-hd{background:#f4f3f0; border-bottom:1px solid #e1e0d9; border-right:1px solid #e1e0d9;
    padding:9px 10px; display:flex; gap:9px; align-items:center; min-width:0}
.pl-pb-hd:last-child{border-right:0}
.pl-pb-hd-first{flex-direction:column; align-items:flex-start; gap:1px; position:sticky; left:0; z-index:2}
.pl-pb-hd-first b{font-size:13.5px; color:#0b0b0b}
.pl-pb-hd-first small{color:#898781; font-weight:600; font-size:11.5px}
.pl-pb-hd-off{background:#f9e4e4}
.pl-pb-hd-off .pl-pb-hd-tx b{color:#b32e2e}
.pl-pb-hd-off .pl-pb-hd-tx small{color:#b32e2e; opacity:.8}
.pl-pb-av{width:30px; height:30px; border-radius:50%; background:#0b0b0b; color:#fff;
    display:flex; align-items:center; justify-content:center; font-weight:800; font-size:12px; flex:none}
.pl-pb-hd-tx{display:flex; flex-direction:column; min-width:0; line-height:1.2}
.pl-pb-hd-tx b{font-size:13.5px; color:#0b0b0b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis}
.pl-pb-hd-tx small{color:#898781; font-weight:600; font-size:11px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis}

/* ---- week label column ---- */
.pl-pb-wk{padding:10px 12px; background:#f4f3f0; border-bottom:1px solid #e1e0d9; border-right:1px solid #e1e0d9;
    display:flex; flex-direction:column; gap:2px; position:sticky; left:0; z-index:1}
.pl-pb-wk b{font-size:13.5px; color:#0b0b0b}
.pl-pb-wk small{color:#898781; font-weight:600; font-size:11.5px}
.pl-pb-wk.pl-pb-cur{box-shadow:inset 4px 0 0 #0b0b0b}
.pl-pb-wk.pl-pb-past b, .pl-pb-wk.pl-pb-past small{opacity:.55}
.pl-pb-now{align-self:flex-start; margin-top:4px; background:#0b0b0b; color:#fff; font-size:10px; font-weight:800;
    letter-spacing:.04em; text-transform:uppercase; border-radius:4px; padding:1px 6px}

/* ---- cells ---- */
.pl-pb-cell{border-bottom:1px solid #e1e0d9; border-right:1px solid #e1e0d9; padding:8px;
    display:flex; flex-direction:column; gap:7px; min-width:0; background:#fcfcfb}
.pl-pb-cell.pl-pb-cur{background:linear-gradient(rgba(11,11,11,.025),rgba(11,11,11,.025)), #fcfcfb}
.pl-pb-cell.pl-pb-past{background:#f7f7f4}
.pl-pb-cell.pl-pb-past .pl-pb-ch{opacity:.6}
.pl-pb-cell-off{background:#fdf6f6}
.pl-pb-cell-off.pl-pb-past{background:#f8efef}

.pl-pb-ch{display:flex; flex-direction:column; gap:4px}
.pl-pb-ch-r1{display:flex; justify-content:space-between; align-items:baseline; gap:6px}
.pl-pb-ch-h{font-weight:800; font-size:12.5px; color:#0b0b0b; font-variant-numeric:tabular-nums; white-space:nowrap}
.pl-pb-ch-n{color:#898781; font-size:11px; font-weight:600; white-space:nowrap}
.pl-pb-track{display:block; flex:none; width:100%; height:6px}
.pl-pb-ch-free{font-size:11.5px; font-weight:700; color:#0a7a0a}
.pl-pb-ch-free.pl-pb-warn{color:#8a5a00}
.pl-pb-ch-free.pl-pb-over{color:#b32e2e}
.pl-pb-ch-free.pl-pb-none{color:#898781}
.pl-pb-ch-free:empty{display:none}

.pl-pb-list{display:flex; flex-direction:column; gap:5px; min-height:38px; flex:1}
.pl-pb-more{border:1px dashed #d9d7ce; background:transparent; color:#52514e; font-weight:700; font-size:11.5px;
    border-radius:6px; padding:4px 8px; cursor:pointer; font-family:inherit}
.pl-pb-more:hover{border-color:#0b0b0b; color:#0b0b0b}
.pl-pb-free{color:#c9c8c0; font-weight:700; font-size:12px; text-align:center; padding:8px 0; user-select:none}

/* ---- legend ---- */
.pl-pb-legend{display:flex; gap:14px; align-items:center; margin-top:10px; font-size:12.5px;
    font-weight:600; color:#52514e; flex-wrap:wrap}
.pl-pb-legend span{display:flex; align-items:center; gap:6px}
.pl-pb-legend i{width:15px; height:15px; border-radius:5px; display:inline-block}
.pl-pb-lg-typed{border:1.5px dashed #6b6a66; background:transparent}
.pl-pb-legend-hint{color:#898781}
</style>
